import { useEffect, useState } from "react";
import { Loader2, Paintbrush, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { HUD } from "@/components/admin/jarvis/theme";
import { FINISH_LABEL } from "@/components/builder/builderLib";

const num = "w-24 h-9 rounded-lg px-2 text-sm border";
const TIERS = [["basic", "Basic colors (gloss, satin, matte, metallic)"], ["colorshift", "Color shift"], ["iridescent", "Iridescent"], ["chrome", "Chrome"]];

const AddonRow = ({ a, set, remove }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`builder-admin-addon-${a.id || "new"}`}>
    <input value={a.name} onChange={(e) => set({ ...a, name: e.target.value })} className={`flex-1 min-w-[160px] h-9 rounded-lg px-2 text-sm border ${HUD.input}`} />
    <input type="number" value={a.price} onChange={(e) => set({ ...a, price: e.target.value })} className={`${num} ${HUD.input}`} />
    <label className="flex items-center gap-1.5 text-[11px] text-gray-400"><Switch checked={a.colors} onCheckedChange={(v) => set({ ...a, colors: v })} />Color pick</label>
    <label className="flex items-center gap-1.5 text-[11px] text-gray-400"><Switch checked={a.active} onCheckedChange={(v) => set({ ...a, active: v })} />Shown</label>
    <button onClick={remove} className="text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
  </div>
);

const ColorRow = ({ c, set, remove }) => (
  <div className="flex flex-wrap items-center gap-2">
    <input type="color" value={c.hex} onChange={(e) => set({ ...c, hex: e.target.value })} className="w-9 h-9 rounded bg-transparent" />
    {(c.finish === "colorshift" || c.finish === "iridescent") && <input type="color" value={c.hex2 || "#888888"} onChange={(e) => set({ ...c, hex2: e.target.value })} className="w-9 h-9 rounded bg-transparent" />}
    <input value={c.name} onChange={(e) => set({ ...c, name: e.target.value })} className={`flex-1 min-w-[140px] h-9 rounded-lg px-2 text-sm border ${HUD.input}`} />
    <select value={c.finish} onChange={(e) => set({ ...c, finish: e.target.value })} className={`h-9 rounded-lg px-2 text-sm border ${HUD.input}`}>
      {Object.entries(FINISH_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
    <button onClick={remove} className="text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
  </div>
);

const upd = (list, i, v) => list.map((x, j) => (j === i ? v : x));

export const BuilderManager = () => {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showColors, setShowColors] = useState(false);
  useEffect(() => { api.get("/admin/builder/config").then((r) => setC(r.data)).catch(() => toast.error("Couldn't load the car builder")); }, []);
  if (!c) return <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />;
  const save = () => {
    setSaving(true);
    api.put("/admin/builder/config", { labor: c.labor, materials: c.materials, addons: c.addons, colors: c.colors })
      .then((r) => { setC(r.data); toast.success("Car builder prices saved"); }).catch((e) => toast.error(e?.response?.data?.detail || "Save failed")).finally(() => setSaving(false));
  };
  return (
    <div className={`${HUD.panel} p-5 space-y-5`} data-testid="builder-admin">
      <div>
        <h3 className="text-white font-['Outfit'] text-lg flex items-center gap-2"><Paintbrush className="w-4 h-4 text-cyan-300" />"Let's Build My Car" prices</h3>
        <p className="text-xs text-gray-400 mt-1">Customers see this on the Services page. Total = wrap labor + material for the finish they pick + every add-on.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="flex items-center justify-between gap-3 text-sm text-gray-300">Wrap labor<input type="number" value={c.labor} onChange={(e) => setC({ ...c, labor: e.target.value })} className={`${num} ${HUD.input}`} data-testid="builder-admin-labor" /></label>
        {TIERS.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between gap-3 text-sm text-gray-300">{l}<input type="number" value={c.materials[k]} onChange={(e) => setC({ ...c, materials: { ...c.materials, [k]: e.target.value } })} className={`${num} ${HUD.input}`} data-testid={`builder-admin-material-${k}`} /></label>
        ))}
      </div>
      <div className="space-y-2">
        <p className={HUD.label}>Add-ons</p>
        {c.addons.map((a, i) => <AddonRow key={i} a={a} set={(v) => setC({ ...c, addons: upd(c.addons, i, v) })} remove={() => setC({ ...c, addons: c.addons.filter((_, j) => j !== i) })} />)}
        <button onClick={() => setC({ ...c, addons: [...c.addons, { id: "", name: "New add-on", price: 0, colors: false, active: true }] })} className={`${HUD.ghost} h-8 px-3 text-xs flex items-center gap-1.5`} data-testid="builder-admin-add-addon"><Plus className="w-3.5 h-3.5" />Add-on</button>
      </div>
      <div className="space-y-2">
        <button onClick={() => setShowColors(!showColors)} className={`${HUD.label} underline`} data-testid="builder-admin-toggle-colors">Wrap colors ({c.colors.length}) — {showColors ? "hide" : "edit"}</button>
        {showColors && <>
          {c.colors.map((x, i) => <ColorRow key={i} c={x} set={(v) => setC({ ...c, colors: upd(c.colors, i, v) })} remove={() => setC({ ...c, colors: c.colors.filter((_, j) => j !== i) })} />)}
          <button onClick={() => setC({ ...c, colors: [...c.colors, { name: "New color", hex: "#3366cc", hex2: "", finish: "gloss" }] })} className={`${HUD.ghost} h-8 px-3 text-xs flex items-center gap-1.5`}><Plus className="w-3.5 h-3.5" />Color</button>
        </>}
      </div>
      <button onClick={save} disabled={saving} className={`${HUD.btn} h-10 px-5 text-sm flex items-center gap-2`} data-testid="builder-admin-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />}Save builder prices</button>
    </div>
  );
};
